import React, { useEffect, useMemo } from 'react'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { ContentCard, Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { StyledBiennale } from 'components'
import { LinearProgress } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useLazyGetTotalRewardToClaimQuery } from 'state/services/indexNodeRewards/indexNodeRewards'
import { useLifetimeTokens } from 'state/global/hooks'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { displayDuration, useCycleDurationTimer } from '../rewards.logic'

dayjs.extend(utc)

const addLeadingZeros = (timeString: string): string => {
  const parts = timeString.split(' ')

  let hours = 0
  let minutes = 0
  let seconds = 0

  // Extract values from timeString where units might be omitted if their value is 0
  parts.forEach((part) => {
    const value = parseInt(part, 10)
    // Check the unit type and update corresponding variable
    if (part.includes('h')) {
      hours = value
    } else if (part.includes('m')) {
      minutes = value
    } else if (part.includes('s')) {
      seconds = value
    }
  })

  // Ensure leading zeros are added to single-digit values for hours, minutes, and seconds
  const formattedHours = hours < 10 ? `0${hours}` : String(hours)
  const formattedMinutes = minutes < 10 ? `0${minutes}` : String(minutes)
  const formattedSeconds = seconds < 10 ? `0${seconds}` : String(seconds)

  return `${formattedHours}h ${formattedMinutes}m ${formattedSeconds}s`
}

const Progress = styled(LinearProgress)`
  width: 100%;
  border-radius: 12px;
  &.MuiLinearProgress-root {
    color: ${({ theme }) => theme.colors.gray10};
    background: ${({ theme }) => theme.colors.gray8};
    .MuiLinearProgress-bar {
      background: ${({ theme }) => theme.colors.purple};
      border-radius: 12px;
    }
  }
`

const CycleProgressBar: React.FC = () => {
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const isMobile = useThemeMediaQuery('mobile')
  const { timeUntilEnd, progress, endOfCycle } = useCycleDurationTimer()
  const endOfCycleDeps = JSON.stringify(dayjs(endOfCycle).format('DD/MM/YYYY HH:mm'))
  const [getRewardFromService] = useLazyGetTotalRewardToClaimQuery()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]

  const formattedEndOfCycleDateToDisplay = useMemo(() => {
    if (!endOfCycle) return ''
    return dayjs(endOfCycle).utc().format('h:mm A [UTC]')
  }, [endOfCycleDeps])

  useEffect(() => {
    if (timeUntilEnd < 1000 && endOfCycle) {
      getRewardFromService({
        account,
        accessToken
      })
    }
  }, [timeUntilEnd, accessToken, account, endOfCycleDeps, getRewardFromService])

  return (
    <ContentCard fullWidth height="100%" mt="16px" flexDirection="column" justifyContent="flex-end" p="0px">
      <Flex gap="8px" alignItems="center" mb="16px">
        <StyledBiennale variant={TextVariant.BODY_BIG_BOLD}>{t('rewards.untilNextCycle')}</StyledBiennale>
        <InfoIcon
          iconProps={{ size: 18 }}
          tooltipProps={{
            title: t('rewards.untilNextCycleInfo'),
            placement: 'top'
          }}
        />
      </Flex>
      <Flex
        fullWidth
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'flex-end'}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? '12px' : '0'}
      >
        <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.purple} textAlign="center" width="max-content">
          {formattedEndOfCycleDateToDisplay}
        </Text>
        <Flex width={isMobile ? '100%' : '60%'} flexDirection="column">
          <Flex fullWidth justifyContent="space-between" alignItems="center">
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6} textAlign="center">
              {t('rewards.cycleEndsIn')}
            </Text>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.white} textAlign="center">
              {addLeadingZeros(
                displayDuration({
                  time: timeUntilEnd,
                  options: {
                    units: ['h', 'm', 's']
                  }
                })
              )}
            </Text>
          </Flex>
          <Progress variant="determinate" value={progress} />
        </Flex>
      </Flex>
    </ContentCard>
  )
}

export default CycleProgressBar
